import DOMPurify from "dompurify";

type MaybeText = string | null | undefined;

const RICH_TEXT_TAG_PATTERN = /<[a-z][\s\S]*>/i;

const DIARY_ALLOWED_TAGS = [
  "p",
  "br",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "ins",
  "ul",
  "ol",
  "li",
];

const HTML_ENTITIES: Record<string, string> = {
  "&nbsp;": " ",
  "&amp;": "&",
  "&lt;": "<",
  "&gt;": ">",
  "&quot;": '"',
  "&#39;": "'",
};

/**
 * Detects whether a diary field holds the rich-text HTML fragment produced by
 * the new editor or the legacy plain text (which may contain `\n`).
 */
export const isRichTextHtml = (value: MaybeText): boolean =>
  typeof value === "string" && RICH_TEXT_TAG_PATTERN.test(value);

/**
 * Empty editor output such as `<p></p>` or `<p><br></p>` counts as empty.
 * Plain text counts as empty only when it is blank.
 */
export const isEmptyRichText = (value: MaybeText): boolean => {
  if (!value) return true;
  if (!isRichTextHtml(value)) return value.trim().length === 0;
  return richTextToPlainText(value).length === 0;
};

/**
 * Sanitizes the fragment to the tag set the backend accepts, with no attributes.
 */
export const sanitizeDiaryHtml = (html: MaybeText): string => {
  if (!html) return "";
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: DIARY_ALLOWED_TAGS,
    ALLOWED_ATTR: [],
  });
};

const decodeEntities = (text: string): string =>
  text.replace(
    /&nbsp;|&amp;|&lt;|&gt;|&quot;|&#39;/g,
    (entity) => HTML_ENTITIES[entity] ?? entity
  );

/**
 * Converts a diary rich-text fragment into plain text suitable for CSV export.
 * Block ends and `<br>` become `\n`, list items get a bullet prefix, every other
 * tag is stripped and common entities are decoded. Plain text passes through
 * unchanged.
 */
export const richTextToPlainText = (html: MaybeText): string => {
  if (!html) return "";
  if (!isRichTextHtml(html)) return html;

  const text = sanitizeDiaryHtml(html)
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<li[^>]*>/gi, "• ")
    .replace(/<\/(p|li|ul|ol)>/gi, "\n")
    .replace(/<[^>]+>/g, "");

  return decodeEntities(text)
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .join("\n");
};
